import { FC, useEffect, useMemo, useState } from 'react';

import {
    Box,
    Divider,
    FormControl,
    FormControlLabel,
    Grid,
    Radio,
    RadioGroup,
    Stack,
    Typography,
} from '@mui/material';
import { FormikProps, useField } from 'formik';
import { AiOutlineMinusCircle } from 'react-icons/ai';
import { Address } from 'types/graphql';

import { InputBaseField } from 'src/components/common/form/FormInput';
import CountrySelectInput from 'src/components/CountrySelectInput/CountrySelectInput';
import { Flex } from 'src/components/Flex';
import { PhoneNumberField } from 'src/components/FormikComponents';
import NPIAutoComplete from 'src/components/FormikComponents/NPIAutoComplete/NPIAutoComplete';
import { InputWithSearch } from 'src/components/InputWithSearch';
import Loader from 'src/components/Loader/Loader';
import MainCard from 'src/components/MainCard/MainCard';
import { AddressListModal } from 'src/components/Modals';
import { useAddMerchantContext } from 'src/contexts/AddMerchantProvider';
import { useAddressLookUp, useBankValidation, useValidateEmail } from 'src/hooks';
import { getCountryName } from 'src/lib/countryConfig';
import getPostalLabel from 'src/lib/postalLabel';
import { ServiceChargeCalculatedAt } from 'src/types/AddMerchant/merchantInfo';
import { maskString } from 'src/utils/validations';

type OpenModalState = {
    open: boolean;
    context?: string;
};

interface OutletComponent {
    formik: FormikProps<any>;
    index: number;
    remove: <X = any>(index: number) => X;
    isServiceApplication?: boolean;
}

const OutletInformationComponent: FC<OutletComponent> = ({
    formik,
    index,
    remove,
    isServiceApplication,
}) => {
    const { merchantApplication, selectedOutlet, setSelectedOutlet, setIsExistingOutlet } =
        useAddMerchantContext();

    const postalLabel = getPostalLabel(merchantApplication?.partner?.countryDetails?.alpha2Code);
    const { outlets } = merchantApplication.merchantInformation;
    const outletOptions = useMemo(() => {
        return outlets.map((outlet) => {
            return {
                id: outlet.name,
                label: outlet.name,
                value: outlet.name,
            };
        });
    }, [outlets]);

    // Modal state management
    const [openAddressModal, setOpenAddressModal] = useState<OpenModalState>({
        open: false,
    });

    const [maskedIBAN, setMaskedIBAN] = useState<string | null>();
    // Address data
    const { addresses, findAddress, searchingForAddress } = useAddressLookUp();

    // IBAN validation
    const { onValidateIBAN, validatingIBAN } = useBankValidation();

    // Radio button field
    const [field] = useField({
        name: `outlets[${index}].bankingDetails.serviceChargeCalculatedAt`,
    });
    const [existingOutlet] = useField({
        name: `existingOutlet`,
    });

    const { validatingEmail, onValidateEmail } = useValidateEmail();

    const [serviceChargeLevel, setServiceChargeLevel] = useState<ServiceChargeCalculatedAt>(
        ServiceChargeCalculatedAt.GROUPLEVEL
    );
    const [showBankForm, setShowBankForm] = useState(false);

    const onPostalCodeCapture = (eircode) => {
        findAddress(eircode);
        setOpenAddressModal({ open: true });
    };

    const onRetrievalOfAddress = (address: Address, formattedAddress: string) => {
        formik.setFieldValue(`outlets[${index}].tradingAddress.addressLine1`, address.line1);
        formik.setFieldValue(`outlets[${index}].tradingAddress.addressLine2`, address.line2);
        formik.setFieldValue(`outlets[${index}].tradingAddress.townOrCity`, address.city);
        formik.setFieldValue(`outlets[${index}].tradingAddress.county`, address.city);
        formik.setFieldValue(`outlets[${index}].tradingAddress.county`, address.province);
        formik.setFieldValue(`outlets[${index}].tradingAddress.country`, address.countryName);
    };

    const handleValidateIBAN = (iban: string) => {
        onValidateIBAN(
            iban.toUpperCase(),
            () => {
                setMaskedIBAN(maskString(iban));
                formik.setFieldError(
                    `outlets[${index}].bankingDetails.internationalBankAccountNumber`,
                    null
                );
            },
            () => {
                formik.setFieldError(
                    `outlets[${index}].bankingDetails.internationalBankAccountNumber`,
                    'IBAN is Invalid'
                );
            }
        );
    };

    useEffect(() => {
        if (serviceChargeLevel === ServiceChargeCalculatedAt.OUTLETLEVEL) {
            setShowBankForm(true);
        } else {
            setShowBankForm(false);
        }
    }, [serviceChargeLevel]);

    // Initialise the service charge level and masked IBAN value with the saved form data
    useEffect(() => {
        if (merchantApplication) {
            setServiceChargeLevel(
                merchantApplication?.merchantInformation?.outlets?.[index]?.bankingDetails
                    ?.serviceChargeCalculatedAt ?? ServiceChargeCalculatedAt.GROUPLEVEL
            );

            const savedIBAN =
                merchantApplication?.merchantInformation?.outlets?.[index]?.bankingDetails
                    ?.internationalBankAccountNumber ?? null;

            savedIBAN && setMaskedIBAN(maskString(savedIBAN));

            if (outlets.length === 1) {
                const firstOutletName =
                    merchantApplication?.merchantInformation?.aboutBusiness?.tradingName;
                setSelectedOutlet(firstOutletName);
                setIsExistingOutlet(true);
            }
        }

        if (isServiceApplication) {
            const defaultOutlet = {
                name: existingOutlet.value === 'Yes' ? selectedOutlet || '' : '',
                siteContact: {
                    email: '',
                    firstName: '',
                    lastName: '',
                    position: '',
                    mobileNumber: '',
                    telephoneNumber: '',
                },
                tradingAddress: {
                    county: '',
                    country: getCountryName(
                        merchantApplication?.partner?.countryDetails?.alpha2Code
                    ),
                    eircode: '',
                    addressLine1: '',
                    addressLine2: '',
                    townOrCity: '',
                },
            };

            formik.setFieldValue(`outlets`, [defaultOutlet]);
            setIsExistingOutlet(existingOutlet.value === 'Yes');
        }
    }, [
        merchantApplication,
        setSelectedOutlet,
        setIsExistingOutlet,
        existingOutlet.value,
        isServiceApplication,
        selectedOutlet,
        outlets,
    ]);

    const validateEmail = async (email) => {
        await onValidateEmail(
            email,
            () => {
                return formik.setFieldError(
                    `outlets[${index}].siteContact.email`,
                    'Provide a valid email address'
                );
            },
            () => {
                return formik.setFieldError(`outlets[${index}].siteContact.email`, null);
            }
        );
    };

    return (
        <>
            {(validatingEmail || searchingForAddress || validatingIBAN) && <Loader />}
            <MainCard>
                <Typography fontSize="24px" color="#1D1D1B" marginBottom="8px">
                    {isServiceApplication ? 'Outlet Information' : `Outlet ${index + 1}`}
                </Typography>
                <Divider
                    sx={{
                        borderColor: 'warning.main',
                        marginBottom: '34px',
                    }}
                />
                <Grid container gap="24px" direction="column">
                    <Grid container rowSpacing={2} columnSpacing={2} rowGap={2}>
                        {!isServiceApplication && (
                            <Grid item width="60%">
                                <Grid
                                    item
                                    xs={12}
                                    md={12}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <Typography fontSize="14px" color="#575757">
                                        Service charges Calculated At
                                    </Typography>

                                    <FormControl component="fieldset">
                                        <RadioGroup
                                            aria-labelledby="service-charge-level-radio-buttons"
                                            name={field.name}
                                            row
                                            value={
                                                field.value || ServiceChargeCalculatedAt.GROUPLEVEL
                                            }
                                            onChange={(e) => {
                                                setServiceChargeLevel(
                                                    ServiceChargeCalculatedAt[e.target.value]
                                                );
                                                formik.setFieldValue(e.target.name, e.target.value);
                                            }}
                                        >
                                            <FormControlLabel
                                                value={ServiceChargeCalculatedAt.GROUPLEVEL}
                                                control={<Radio sx={{ color: '#F39E4E' }} />}
                                                label="Group Level"
                                            />
                                            <FormControlLabel
                                                value={ServiceChargeCalculatedAt.COMPANYLEVEL}
                                                control={<Radio sx={{ color: '#F39E4E' }} />}
                                                label="Company Level"
                                            />
                                            <FormControlLabel
                                                value={ServiceChargeCalculatedAt.OUTLETLEVEL}
                                                control={<Radio sx={{ color: '#F39E4E' }} />}
                                                label="Outlet Level"
                                            />
                                        </RadioGroup>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        )}

                        <Grid
                            container
                            width="100%"
                            display={isServiceApplication ? 'flex' : 'grid'}
                        >
                            {isServiceApplication && (
                                <Grid item xs={12} lg={6} px={2}>
                                    <FormControl component="fieldset" width="100%">
                                        <Flex alignItems={'center'} gap={2}>
                                            <Typography variant="subtitle1">
                                                Existing Outlet?{' '}
                                            </Typography>
                                            <RadioGroup
                                                aria-labelledby="service-existing-outlet-radio-buttons"
                                                name={existingOutlet.name}
                                                row
                                                data-testid={`existing-outlet-${index}`}
                                                value={existingOutlet.value || 'Yes'}
                                                onChange={(e) => {
                                                    formik.setFieldValue(
                                                        e.target.name,
                                                        e.target.value
                                                    );
                                                }}
                                            >
                                                <FormControlLabel
                                                    value={'Yes'}
                                                    control={<Radio sx={{ color: '#F39E4E' }} />}
                                                    label="Yes"
                                                />
                                                <FormControlLabel
                                                    value={'No'}
                                                    control={<Radio sx={{ color: '#F39E4E' }} />}
                                                    label="No"
                                                />
                                            </RadioGroup>
                                        </Flex>
                                    </FormControl>
                                </Grid>
                            )}
                            {isServiceApplication && existingOutlet.value === 'Yes' && (
                                <Grid
                                    item
                                    xs={isServiceApplication ? 12 : 8}
                                    lg={isServiceApplication ? 6 : 4}
                                    px={isServiceApplication ? 0 : 2}
                                    gap={0}
                                    display="flex"
                                    flexDirection="column"
                                >
                                    <NPIAutoComplete
                                        header="Outlet Name"
                                        name="outlets[0].name"
                                        options={outletOptions}
                                        placeholder="Please select an outlet"
                                        disabled={outlets.length === 1}
                                        onChange={(value) => {
                                            formik.setFieldValue('outlets[0].name', value?.value);
                                            setSelectedOutlet(value?.value);
                                        }}
                                    />
                                </Grid>
                            )}
                            {existingOutlet.value == 'No' || !isServiceApplication ? (
                                <Grid
                                    item
                                    xs={isServiceApplication ? 12 : 8}
                                    lg={isServiceApplication ? 6 : 4}
                                    px={isServiceApplication ? 0 : 2}
                                >
                                    <InputBaseField
                                        label="Outlet Name"
                                        name={`outlets[${index}].name`}
                                        placeholder="Enter Outlet Name"
                                    />
                                </Grid>
                            ) : null}
                        </Grid>
                    </Grid>
                    {existingOutlet.value == 'No' || !isServiceApplication ? (
                        <>
                            {' '}
                            <Typography
                                fontSize="20px"
                                fontWeight="500"
                                color="primary"
                                marginBottom="8px"
                            >
                                Trading Address
                            </Typography>
                            <Box>
                                <Stack spacing={4}>
                                    <Box>
                                        <Grid container spacing={3} xs={12}>
                                            <Grid item width="100%">
                                                <Grid item xs={12} lg={6}>
                                                    <InputWithSearch
                                                        searchText="Search"
                                                        onSearch={onPostalCodeCapture}
                                                        placeholder={`Enter ${postalLabel}`}
                                                        label={postalLabel}
                                                        fieldName={`outlets[${index}].tradingAddress.eircode`}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                    <Box>
                                        <Grid container spacing={3}>
                                            <Grid item xs={12} md={6}>
                                                <InputBaseField
                                                    label="Address Line 1"
                                                    name={`outlets[${index}].tradingAddress.addressLine1`}
                                                    placeholder="Address Line 1"
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <InputBaseField
                                                    label="Address Line 2"
                                                    name={`outlets[${index}].tradingAddress.addressLine2`}
                                                    placeholder="Address Line 2"
                                                />
                                            </Grid>
                                        </Grid>
                                    </Box>
                                    <Box>
                                        <Grid container spacing={3}>
                                            <Grid item xs={12} md={6}>
                                                <InputBaseField
                                                    label="Town/City"
                                                    name={`outlets[${index}].tradingAddress.townOrCity`}
                                                    placeholder="Enter Town/City"
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <InputBaseField
                                                    label="County"
                                                    name={`outlets[${index}].tradingAddress.county`}
                                                    placeholder="Enter County"
                                                />
                                            </Grid>
                                        </Grid>
                                    </Box>
                                    {/* Row 4 */}
                                    <Box>
                                        <Grid container spacing={3} xs={12}>
                                            <Grid item width="100%">
                                                <Grid item xs={12} lg={6}>
                                                    <CountrySelectInput
                                                        setFieldValue={formik.setFieldValue}
                                                        fieldName={`outlets[${index}].tradingAddress.country`}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </Stack>
                            </Box>
                            <Typography
                                fontSize="20px"
                                fontWeight="500"
                                color="primary"
                                marginTop="8px"
                                marginBottom="8px"
                            >
                                Site Contact
                            </Typography>
                            <Grid container rowSpacing={2} columnSpacing={2} rowGap={2}>
                                <Grid item xs={12} lg={6}>
                                    <InputBaseField
                                        label="First Name"
                                        name={`outlets[${index}].siteContact.firstName`}
                                        placeholder="Enter First Name"
                                    />
                                </Grid>
                                <Grid item xs={12} lg={6}>
                                    <InputBaseField
                                        label="Last Name"
                                        name={`outlets[${index}].siteContact.lastName`}
                                        placeholder="Enter Last Name"
                                    />
                                </Grid>
                                <Grid item xs={12} lg={6}>
                                    <InputWithSearch
                                        searchText="Validate"
                                        onSearch={async (email) => {
                                            return validateEmail(email);
                                        }}
                                        placeholder="Enter Email"
                                        label="Email"
                                        fieldName={`outlets[${index}].siteContact.email`}
                                    />
                                </Grid>

                                <Grid item xs={12} lg={6}>
                                    <PhoneNumberField
                                        formikFieldName={`outlets[${index}].siteContact.mobileNumber`}
                                        label="Mobile Number"
                                        placeholder="Enter Mobile Number"
                                        searchText="Validate"
                                    />
                                </Grid>

                                <Grid item xs={12} lg={6}>
                                    <PhoneNumberField
                                        formikFieldName={`outlets[${index}].siteContact.telephoneNumber`}
                                        searchText="Validate"
                                        label="Telephone Number"
                                    />
                                </Grid>

                                <Grid item xs={12} lg={6}>
                                    <InputBaseField
                                        label="Position"
                                        name={`outlets[${index}].siteContact.position`}
                                        placeholder="Enter Position"
                                    />
                                </Grid>
                            </Grid>{' '}
                        </>
                    ) : null}

                    {showBankForm && !isServiceApplication && (
                        <Grid>
                            <Typography
                                fontSize="18px"
                                color="primary"
                                marginTop="40px"
                                marginBottom="8px"
                            >
                                Bank Details
                            </Typography>
                            <Divider
                                sx={{
                                    borderColor: 'warning.main',
                                    marginBottom: '34px',
                                }}
                            />
                            <Grid container rowSpacing={2} columnSpacing={2} rowGap={2}>
                                <Grid item xs={12} lg={6}>
                                    <InputBaseField
                                        label="BIC"
                                        name={`outlets[${index}].bankingDetails.bankIdentifierCode`}
                                        placeholder="Enter BIC"
                                    />
                                </Grid>
                                <Grid item xs={12} lg={6}>
                                    <InputWithSearch
                                        searchText="Validate"
                                        onSearch={handleValidateIBAN}
                                        placeholder="Enter IBAN Number"
                                        label="IBAN"
                                        fieldName={`outlets[${index}].bankingDetails.internationalBankAccountNumber`}
                                        maskedValue={maskedIBAN}
                                        clearMaskedValue={() => {
                                            return setMaskedIBAN(null);
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12} lg={6}>
                                    <InputBaseField
                                        label="Account Name"
                                        name={`outlets[${index}].bankingDetails.accountName`}
                                        placeholder="Enter Account Name"
                                    />
                                </Grid>
                            </Grid>
                            <Typography color="#8E8E8D" marginTop="16px">
                                A separate proof of bank will be needed for the above bank details.
                                Please make sure you upload this in the supporting documents step.
                            </Typography>
                        </Grid>
                    )}
                </Grid>

                {index > 0 && (
                    <Box
                        display="flex"
                        alignItems="center"
                        marginTop="24px"
                        onClick={() => {
                            remove(index);

                            formik.setFieldValue(
                                'numberOfOutlets',
                                formik.values.numberOfOutlets - 1
                            );
                        }}
                        sx={{ cursor: 'pointer' }}
                    >
                        <AiOutlineMinusCircle color="#F3A256" size="24px" />
                        <Typography
                            fontSize="16px"
                            color="#575757"
                            lineHeight="24px"
                            fontWeight="400"
                            marginLeft="8px"
                        >
                            Remove Outlet
                        </Typography>
                    </Box>
                )}
            </MainCard>
            <AddressListModal
                isOpen={openAddressModal.open}
                onClose={() => {
                    return setOpenAddressModal((prev) => {
                        return { ...prev, open: false };
                    });
                }}
                addressList={addresses}
                onSelectedAddress={onRetrievalOfAddress}
            />
        </>
    );
};

export default OutletInformationComponent;
